import { type ReactNode, Suspense, useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export interface RouteTab {
  key: string
  label: string
  /** Rendered before the label — a lucide icon. */
  icon?: ReactNode
  content: () => ReactNode
}

export interface RouteTabsProps {
  tabs: RouteTab[]
  /** Query-param name, defaults to "tab" */
  param?: string
  /** Optional content rendered to the right of the tab buttons (e.g. a dropdown) */
  headerRight?: ReactNode
}

/**
 * Route tabs that sync with a URL search parameter, built on the shadcn/Radix
 * {@link Tabs} primitive so keyboard behaviour and ARIA wiring come from Radix.
 *
 * A tab's content is mounted the first time it is opened and kept mounted after
 * that, so its scroll position and local state survive switching away — hence
 * `forceMount` on the panels rather than letting Radix unmount them.
 *
 * `forceMount` also stops Radix hiding the panels it is not showing, so the
 * inactive ones are hidden here instead; without it every visited tab stacks up
 * on the page at once.
 */
export function RouteTabs({ tabs, param = 'tab', headerRight }: RouteTabsProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [mounted, setMounted] = useState<Set<string>>(() => new Set())

  const activeKey = useMemo(() => {
    const fromUrl = searchParams.get(param)
    if (fromUrl && tabs.some((t) => t.key === fromUrl)) return fromUrl
    return tabs[0]?.key ?? ''
  }, [searchParams, param, tabs])

  const setActiveKey = useCallback(
    (key: string) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (key === tabs[0]?.key) p.delete(param)
          else p.set(param, key)
          return p
        },
        { replace: true }
      )
    },
    [setSearchParams, param, tabs]
  )

  // Track which tabs have been visited so content stays mounted (keeps scroll/state)
  if (!mounted.has(activeKey)) {
    setMounted((prev) => new Set(prev).add(activeKey))
  }

  return (
    <Tabs value={activeKey} onValueChange={setActiveKey} className="min-w-0 flex-1 gap-0">
      <div className="flex items-center justify-between gap-3 border-b border-(--ad-border) px-4 py-3">
        <TabsList>
          {tabs.map((tab) => (
            <TabsTrigger key={tab.key} value={tab.key}>
              {tab.icon}
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {headerRight && <div className="flex items-center gap-2">{headerRight}</div>}
      </div>

      {tabs.map((tab) =>
        mounted.has(tab.key) ? (
          <TabsContent
            key={tab.key}
            value={tab.key}
            forceMount
            className={cn('min-w-0 p-4', tab.key !== activeKey && 'hidden')}
          >
            <Suspense
              fallback={
                <div className="flex flex-col gap-3 p-6">
                  <Skeleton className="h-4 w-2/5" />
                  <Skeleton className="h-9 w-full rounded-md" />
                  <Skeleton className="h-9 w-full rounded-md" />
                </div>
              }
            >
              {tab.content()}
            </Suspense>
          </TabsContent>
        ) : null
      )}
    </Tabs>
  )
}
